/* eslint-disable no-console */
import { VirtualBg } from '../../stores/types/content';

// BG mode values — must match the constants in the worker and COMPOSITE_FRAG_SRC.
const BG_PASSTHROUGH = 0;
const BG_IMAGE       = 1;
const BG_BLUR        = 2;

/**
 * MediaPipe-based virtual background pipeline — Web Worker architecture.
 *
 * Architecture:
 *   raw camera MediaStream
 *     → MediaStreamTrackProcessor → ReadableStream<VideoFrame>
 *       ──── transferred to worker ────→
 *         worker: drawImage → MediaPipe segmentForVideo → WebGL2 composite
 *         worker: new VideoFrame(outputCanvas) → WritableStream<VideoFrame>
 *       ←── back on main thread ────
 *     → MediaStreamTrackGenerator.track → MediaStream → OT publisher
 *
 * All segmentation and GPU compositing runs in the worker thread.
 * The main thread does zero ML/GPU work — just proxies messages and
 * manages stream plumbing.
 *
 * Public API is identical to the previous main-thread implementation so
 * opentok-connection.ts requires no changes.
 */
export default class MediaPipeBackground {
	private worker: Worker | null = null;
	private generator: MediaStreamTrackGenerator | null = null;
	private rawStream: MediaStream | null = null;
	public  canvasStream: MediaStream | null = null;

	constructor() {
		// Intentionally empty — all init work is async in init().
	}

	// ─── Public API ──────────────────────────────────────────────────────────

	/**
	 * Start the pipeline. Await this before passing canvasStream to OT.
	 *
	 * @param rawStream  Raw camera-only MediaStream (no audio needed).
	 * @param bg         Optional background to apply immediately.
	 * @returns          The generator-derived MediaStream to use as OT videoSource.
	 * @throws           If MediaStreamTrackProcessor/Generator are unavailable.
	 */
	async init(rawStream: MediaStream, bg?: VirtualBg | null): Promise<MediaStream> {
		if (typeof MediaStreamTrackProcessor === 'undefined' || typeof MediaStreamTrackGenerator === 'undefined') {
			throw new Error('MediaPipeBackground: Insertable Streams API not available');
		}

		this.rawStream = rawStream;

		const videoTrack = rawStream.getVideoTracks()[0];
		const settings = videoTrack?.getSettings() ?? {};
		const width  = settings.width  ?? 1280;
		const height = settings.height ?? 720;

		// Set up the Insertable Streams pipeline.
		const processor = new MediaStreamTrackProcessor({ track: videoTrack });
		this.generator  = new MediaStreamTrackGenerator({ kind: 'video' });

		const readable = processor.readable;
		const writable = this.generator.writable;

		// Determine initial background mode.
		let initBgMode = BG_PASSTHROUGH;
		let initBlur: 'high' | 'low' | null = null;
		let initBitmap: ImageBitmap | null = null;

		if (bg) {
			if (bg.blur) {
				initBgMode = BG_BLUR;
				initBlur = bg.blur as 'high' | 'low';
			} else if (bg.url) {
				initBgMode = BG_IMAGE;
				initBitmap = await this.loadImageBitmap(bg.url);
			}
		}

		// webpack 5 native worker — avoids worker-loader which has child
		// compiler issues with heavy WASM dependencies like @mediapipe/tasks-vision.
		// The `new URL(...)` pattern is detected statically by webpack 5 and
		// creates a proper worker bundle with correct chunk-loading support.
		this.worker = new Worker(
			/* webpackChunkName: "mediapipe-bg-worker" */
			new URL('../../workers/mediapipe-background.worker', import.meta.url),
		);

		// Wait for the worker to signal ready.
		const readyPromise = new Promise<void>((resolve, reject) => {
			const handler = (e: MessageEvent) => {
				if (e.data.type === 'ready') {
					this.worker?.removeEventListener('message', handler);
					resolve();
				} else if (e.data.type === 'error') {
					this.worker?.removeEventListener('message', handler);
					reject(new Error(e.data.error));
				}
			};
			this.worker!.addEventListener('message', handler);
		});

		// Build transfer list — zero-copy for streams and bitmaps.
		const transferList: Transferable[] = [readable, writable];
		if (initBitmap) transferList.push(initBitmap);

		this.worker.postMessage({
			type: 'init',
			readable,
			writable,
			width,
			height,
			bgMode: initBgMode,
			blurStrength: initBlur,
			bgImageBitmap: initBitmap,
		}, transferList);

		await readyPromise;

		// Listen for visibility changes to forward to the worker.
		document.addEventListener('visibilitychange', this.onVisibilityChange);

		this.canvasStream = new MediaStream([this.generator]);
		return this.canvasStream;
	}

	/** True once init() has completed and the output stream is live. */
	get isActive(): boolean {
		return this.canvasStream !== null;
	}

	/** Update the active background. Safe to call at any time after init(). */
	async setBackground(bg: VirtualBg | null): Promise<void> {
		if (!this.worker) return;

		if (!bg) {
			this.worker.postMessage({ type: 'setBackground', bgMode: BG_PASSTHROUGH });
			return;
		}

		if (bg.blur) {
			this.worker.postMessage({
				type: 'setBackground',
				bgMode: BG_BLUR,
				blurStrength: bg.blur === 'high' ? 'high' : 'low',
			});
		} else if (bg.url) {
			const bitmap = await this.loadImageBitmap(bg.url);
			this.worker.postMessage({
				type: 'setBackground',
				bgMode: BG_IMAGE,
				bgImageBitmap: bitmap,
			}, [bitmap]);
		}
	}

	/** Remove background — pipeline keeps running, video passes through. */
	clearBackground(): void {
		this.worker?.postMessage({ type: 'setBackground', bgMode: BG_PASSTHROUGH });
	}

	/**
	 * Stop the raw camera tracks so the OS releases the camera hardware
	 * (e.g. macOS camera indicator light turns off). The pipeline stays
	 * alive — call updateRawStream() with a fresh stream to resume.
	 */
	stopRawStream(): void {
		this.rawStream?.getTracks().forEach((t) => t.stop());
		this.rawStream = null;
	}

	/**
	 * Hot-swap the raw camera input without interrupting the output stream.
	 * Use this when the user switches cameras so the OT publisher stays connected.
	 *
	 * Creates a new MediaStreamTrackProcessor for the new track and transfers
	 * its readable to the worker. The WritableStream (generator) is NOT
	 * replaced — the output track stays stable for OT.
	 */
	async updateRawStream(newStream: MediaStream): Promise<void> {
		const old = this.rawStream;
		this.rawStream = newStream;

		const newTrack = newStream.getVideoTracks()[0];
		const settings = newTrack?.getSettings() ?? {};
		const newW = settings.width  ?? 1280;
		const newH = settings.height ?? 720;

		const processor = new MediaStreamTrackProcessor({ track: newTrack });
		const readable = processor.readable;

		this.worker?.postMessage({
			type: 'updateStream',
			readable,
			width: newW,
			height: newH,
		}, [readable]);

		// Release old camera tracks after the worker has the new stream.
		old?.getTracks().forEach((t) => t.stop());
	}

	destroy(): void {
		document.removeEventListener('visibilitychange', this.onVisibilityChange);

		this.worker?.postMessage({ type: 'destroy' });
		this.worker?.terminate();
		this.worker = null;

		// Stop the generator track so OT sees the track end.
		this.generator?.stop();
		this.generator = null;

		this.canvasStream?.getTracks().forEach((t) => t.stop());
		this.canvasStream = null;
		this.rawStream    = null;
	}

	// ─── Private ─────────────────────────────────────────────────────────────

	private onVisibilityChange = (): void => {
		this.worker?.postMessage({ type: 'visibility', hidden: document.hidden });
	};

	/**
	 * Load a background image URL into an ImageBitmap for zero-copy transfer
	 * to the worker.
	 */
	private async loadImageBitmap(url: string): Promise<ImageBitmap> {
		const response = await fetch(url);
		const blob = await response.blob();
		// Flip Y so the bitmap matches WebGL coordinate system (0,0 at bottom-left)
		return createImageBitmap(blob, { imageOrientation: 'flipY' });
	}
}
